import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { LoaderService } from '@h5s/design-system';
import {
  CityTaxCheckInformation,
  GenerateIstatClosureFileRequest,
  GenerateIstatFileRequest,
  GenerateIstatNoMovementsFileRequest,
  GenerateIstatReportFileRequest,
  GetCityTaxCheckInformationRequest,
  Month,
  OwnerIstatProfile,
  SendIstatRequest,
} from '@h5s/entities';
import { FeatureDocService } from '@h5s/feature-doc';
import { today } from '@h5s/util-date';
import { AppError, AppErrorBuilder, Severity } from '@h5s/util-error';
import { LoggerService } from '@h5s/util-logger';
import { hasFilledValues, updateStoreEntity } from '@h5s/util-object';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { map, Observable, of, take } from 'rxjs';
import { FeatureIstatService } from '../../feature-istat.service';
import { GenerationParameters } from '../components/generation-params-modal/models/generation-parameters';
import { IstatFilterModel } from '../components/istat-filter/models/istat-filter-model';
import { TableIstatCustomer } from '../components/istat-table/models/table-istat-customer';

const FEATURE_ISTAT_STORE_NAME = 'FeatureIstatStore';
const MISSING_OWNER_ID_ERROR = 'OwnerIstatId missing: ISTAT sending impossible';

type FeatureIstatState = {
  selectedOwnerIstatProfile: OwnerIstatProfile;
  selectedFilters: IstatFilterModel;
  selectedTableIstatCustomer: TableIstatCustomer[];
  istatProfileIsChanged: boolean;
  ownerIstatProfileList: OwnerIstatProfile[];
  generationParameters: GenerationParameters;
  generationParametersLoaded: boolean;
  areFiltersDirty: boolean;
};

const initialState: FeatureIstatState = {
  selectedOwnerIstatProfile: new OwnerIstatProfile(),
  istatProfileIsChanged: false,
  selectedFilters: new IstatFilterModel(),
  selectedTableIstatCustomer: [],
  ownerIstatProfileList: [],
  generationParameters: new GenerationParameters(),
  generationParametersLoaded: false,
  areFiltersDirty: false,
};

export const FeatureIstatStore = signalStore(
  /** Abilita il debug con Redux */
  withDevtools(FEATURE_ISTAT_STORE_NAME),
  /** Stato di inizio dell'entity */
  withState(initialState),
  /** Dipendenze */
  withProps(() => ({
    logger: inject(LoggerService).getLogger(FEATURE_ISTAT_STORE_NAME),
    featureIstatService: inject(FeatureIstatService),
    featureDocService: inject(FeatureDocService),
    loaderService: inject(LoaderService),
  })),
  withComputed(({ selectedFilters, ownerIstatProfileList }) => ({
    selectedIstatProfileFromFilter: computed(() => {
      const profileId = selectedFilters().profile;
      return ownerIstatProfileList().find((p) => p.id === profileId);
    }),
  })),
  withComputed(
    ({
      selectedOwnerIstatProfile,
      selectedTableIstatCustomer,
      ownerIstatProfileList,
      areFiltersDirty,
      selectedFilters,
      selectedIstatProfileFromFilter,
    }) => ({
      isFilterEmpty: computed(() => {
        return areFiltersDirty() || !hasFilledValues(selectedFilters());
      }),
      selectedIstatType: computed(() => selectedOwnerIstatProfile()?.istatType),

      selectedIstatTypeFromFilter: computed(() => selectedIstatProfileFromFilter()?.istatType),
      selectedOwnerIstatProfileId: computed(() => selectedIstatProfileFromFilter()?.id ?? null),
      isSelectedIstatTypePrintable: computed(() => selectedIstatProfileFromFilter()?.istatType?.printable ?? false),
      isSelectedIstatTypeSendOnline: computed<boolean>(() => {
        const profile = selectedOwnerIstatProfile();

        if (!profile) return false;
        const istatType = profile?.istatType;
        const externalSendOnline = profile.sendOnline;

        /**
         * Logica:
         * - Se non è abilitato sendOnline → false
         * - Se sendFile è true → il risultato dipende da externalSendOnline
         * - Se sendFile è false → true in ogni caso
         */
        if (!istatType?.sendOnline) return false;
        if (istatType.sendFile) return externalSendOnline;
        return true;
      }),

      selectedTableIstatCustomerIds: computed(() => selectedTableIstatCustomer().map((ic) => ic.id)),
      hasSelectedTableIstatCustomer: computed(() => selectedTableIstatCustomer().length > 0),
      showStatusColumn: computed<boolean>(() => ownerIstatProfileList().some((op: OwnerIstatProfile) => op.sendOnline)),
      ownerIstatProfileRecord: computed(() => {
        const list = ownerIstatProfileList();
        return Object.fromEntries(list.map((op) => [op.id, op.description]));
      }),
    }),
  ),
  // Metodi privati
  withMethods(({ logger, ...store }) => ({
    _setSelectedOwnerIstatProfile(selectedOwnerIstatProfile: FeatureIstatState['selectedOwnerIstatProfile']): void {
      patchState(store, {
        selectedOwnerIstatProfile: updateStoreEntity(store.selectedOwnerIstatProfile(), selectedOwnerIstatProfile),
      });
      logger.log(`Set selectedOwnerIstatProfile on store`);
    },
    _setSelectedFilters(selectedFilters: FeatureIstatState['selectedFilters']): void {
      patchState(store, {
        selectedFilters: updateStoreEntity(store.selectedFilters(), selectedFilters),
      });
      logger.log(`Set selectedFilters on store`);
    },
    _setSelectedTableIstatCustomer(selectedTableIstatCustomer: FeatureIstatState['selectedTableIstatCustomer']): void {
      patchState(store, {
        selectedTableIstatCustomer,
      });
    },
    _setIstatProfileIsChanged: (value: boolean): void => {
      patchState(store, { istatProfileIsChanged: value });
    },
    _setOwnerIstatProfileList: (ownerIstatProfileList: OwnerIstatProfile[]): void => {
      patchState(store, {
        ownerIstatProfileList,
      });
    },
    _setGenerationParameters: (generationParameters: GenerationParameters): void => {
      patchState(store, {
        generationParameters,
      });
    },
    _setGenerationParametersLoaded: (generationParametersLoaded: boolean): void => {
      patchState(store, {
        generationParametersLoaded,
      });
    },
    _setAreFiltersDirty: (areFiltersDirty: boolean): void => {
      patchState(store, {
        areFiltersDirty,
      });
    },
  })),
  // Metodi pubblici
  withMethods(({ featureIstatService, loaderService, featureDocService, logger, ...store }) => ({
    updateSelectedOwnerIstatProfile(op: OwnerIstatProfile): void {
      store._setSelectedOwnerIstatProfile(op);
    },
    updateSelectedFilters(selectedFilters: IstatFilterModel): void {
      store._setSelectedFilters(selectedFilters);
    },
    updateSelectedTableIstatCustomer(selectedTableIstatCustomer: TableIstatCustomer[]): void {
      store._setSelectedTableIstatCustomer(selectedTableIstatCustomer);
    },
    updateIstatProfileIsChanged(value: boolean): void {
      store._setIstatProfileIsChanged(value);
    },
    updateGenerationParameters(gp: GenerationParameters): void {
      store._setGenerationParameters(gp);
    },
    updateAreFiltersDirty(state: boolean): void {
      store._setAreFiltersDirty(state);
    },
    resetGenerationParametersLoaded(): void {
      store._setGenerationParametersLoaded(false);
    },
    loadOwnerIstatProfile(): Observable<void> {
      logger.log('Loading OwnerIstatProfile list...');
      return featureIstatService.getOwnerIstatProfileList().pipe(
        take(1), // Prende il primo valore ed esegue il complete
        tapResponse({
          next: (ownerIstatProfileList) => {
            store._setOwnerIstatProfileList(ownerIstatProfileList);
            logger.log('OwnerIstatProfile loaded successfully', ownerIstatProfileList);
          },
          error: (error: AppError) => {
            logger.error('Failed to load OwnerIstatProfile', error);
          },
        }),
        map(() => void 0), // Emette void quando l'operazione è completa
      );
    },
    getOwnerIstatProfileById(id: number): OwnerIstatProfile | null {
      return store.ownerIstatProfileList().find((p) => p.id === id) ?? null;
    },
    loadGenerationParameters(): Observable<void> {
      const selectedOwnerIstatProfileId = store.selectedOwnerIstatProfileId();
      if (selectedOwnerIstatProfileId === null) {
        logger.error('Cannot load Generation Parameters: selectedOwnerIstatProfileId is null');
        return of();
      }

      const ownerId = selectedOwnerIstatProfileId;
      const month = store.selectedFilters.month();
      const year = store.selectedFilters.year();

      if (store.generationParametersLoaded()) {
        logger.log('Generation parameters already loaded, skipping.');
        return of(void 0);
      }

      logger.log(`Loading generation parameters for ID: ${ownerId}, month: ${month}, year: ${year}...`);

      return featureIstatService.getIstatGenerationParametersByOwnerIstatProfileId(ownerId).pipe(
        take(1),
        tapResponse({
          next: (istatGenerationParameters) => {
            const generationParameters = new GenerationParameters();
            generationParameters.availableBeds = istatGenerationParameters.availableBeds;
            generationParameters.availableRooms = istatGenerationParameters.availableRooms;
            generationParameters.availableToilets = istatGenerationParameters.availableToilets;
            generationParameters.openDays = month && year ? Month.getMonthDays(month, year) : null;

            store._setGenerationParameters(generationParameters);
            store._setGenerationParametersLoaded(true);
            logger.log('Generation parameters loaded successfully', istatGenerationParameters);
          },
          error: (error: AppError) => {
            logger.error('Failed to load Generation Parameters', error);
            store._setGenerationParametersLoaded(false);
          },
        }),
        map(() => void 0),
      );
    },
    sendIstatOnline(): Observable<string> {
      const selectedFilters = store.selectedFilters();
      const generationParameters = store.generationParameters();
      const selectedTableIstatCustomerIds = store.selectedTableIstatCustomerIds();

      const ownerIstatId = selectedFilters.profile;

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const istatSendRequest = new SendIstatRequest({
        availableBeds: generationParameters.availableBeds,
        availableRooms: generationParameters.availableRooms,
        istatCustomerIdsToInclude: selectedTableIstatCustomerIds,
        ownerIstatId: ownerIstatId,
        month: selectedFilters.month,
        referenceDate: selectedFilters.day,
        year: selectedFilters.year,
      });

      return featureIstatService.sendIstatOnline(istatSendRequest);
    },
    generateIstatFile(): void {
      const selectedFilters = store.selectedFilters();
      const generationParameters = store.generationParameters();
      const selectedTableIstatCustomerIds = store.selectedTableIstatCustomerIds();

      const ownerIstatId = selectedFilters.profile;

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const istatSendRequest = new GenerateIstatFileRequest({
        ownerIstatId: ownerIstatId,
        istatCustomerIdsToInclude: selectedTableIstatCustomerIds,
        month: selectedFilters.month,
        year: selectedFilters.year,
        referenceDate: selectedFilters.day,
        availableRooms: generationParameters.availableRooms,
        availableBeds: generationParameters.availableBeds,
        availableToilets: generationParameters.availableToilets,
        openingDays: generationParameters.openDays,
      });

      featureDocService
        .openIstatFile(istatSendRequest)
        .pipe(
          take(1),
          map((result) => featureDocService.openReport(() => of(result))),
        )
        .subscribe({
          next: () => {
            featureIstatService.research.next();
          },
          error: () => {
            featureIstatService.research.next();
          },
        });
    },
    generateIstatClosureFile(): void {
      const selectedFilters = store.selectedFilters();
      const ownerIstatId = selectedFilters.profile;

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const istatSendRequest = new GenerateIstatClosureFileRequest({
        ownerIstatId: ownerIstatId,
        referenceDate: selectedFilters.day ?? today(), // Non sarà mai undefined con IstatType == SPOT
      });

      return featureDocService.openIstatClosureFile(istatSendRequest);
    },
    generateIstatNoMovementsFile(): void {
      const selectedFilters = store.selectedFilters();
      const ownerIstatId = selectedFilters.profile;
      const generationParameters = store.generationParameters();

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const istatSendRequest = new GenerateIstatNoMovementsFileRequest({
        ownerIstatId: ownerIstatId,
        referenceDate: selectedFilters.day ?? today(), // Non sarà mai undefined con IstatType == SPOT
        availableRooms: generationParameters.availableRooms,
        availableBeds: generationParameters.availableBeds,
      });

      return featureDocService.openIstatNoMovementsFile(istatSendRequest);
    },
    generateIstatReportFile(): void {
      const selectedFilters = store.selectedFilters();
      const ownerIstatId = selectedFilters.profile;
      const generationParameters = store.generationParameters();
      const selectedTableIstatCustomerIds = store.selectedTableIstatCustomerIds();

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const istatSendRequest = new GenerateIstatReportFileRequest({
        ownerIstatId: ownerIstatId,
        availableBeds: generationParameters.availableBeds,
        availableRooms: generationParameters.availableRooms,
        availableToilets: generationParameters.availableToilets,
        istatCustomerIdsToInclude: selectedTableIstatCustomerIds,
        month: selectedFilters.month,
        referenceDate: selectedFilters.day,
        year: selectedFilters.year,
        beginDate: selectedFilters.startDate,
        endDate: selectedFilters.endDate,
        openingDays: generationParameters.openDays,
      });

      return featureDocService.openIstatReportFile(istatSendRequest);
    },
    getCityTaxCheckInformation(): Observable<CityTaxCheckInformation> {
      const selectedFilters = store.selectedFilters();
      const selectedTableIstatCustomerIds = store.selectedTableIstatCustomerIds();

      const ownerIstatId = selectedFilters.profile;

      if (!ownerIstatId) {
        throw new AppErrorBuilder(`[FeatureIstatStore] ${MISSING_OWNER_ID_ERROR}`, Severity.ERROR).build();
      }

      const request = new GetCityTaxCheckInformationRequest({
        istatCustomerIdsToInclude: selectedTableIstatCustomerIds,
        ownerIstatId: ownerIstatId,
        month: selectedFilters.month,
        referenceDate: selectedFilters.day,
        year: selectedFilters.year,
      });

      const cityTaxCheckInformation$ = featureIstatService.getCityTaxCheckInformation(request);
      loaderService.showLoading({ subscription: cityTaxCheckInformation$.subscribe() });

      return cityTaxCheckInformation$;
    },
  })),
);
